/**
 * Permission label helpers.
 *
 * Frontend never renders raw permission codes to the user. Instead, every
 * permission from `permissions_catalog.PERMISSIONS` has a `perm.<code>` key
 * in each locale file (az/en/ru); the pytest coverage test
 * `test_permission_i18n_coverage.py` guarantees no code lands in the catalog
 * without translations.
 *
 * Usage:
 *   const label = usePermLabel();
 *   <PermLabel code={p} />          // just the label
 *   <PermLabel code={p} showCode /> // label + monospace raw code (dev hint)
 *   <PermCodeHint code={p} />       // tiny standalone monospace pill for admins
 */
import { useTranslation } from "react-i18next";

/** Returns a translator function `(code) => human label` for the active UI language. */
export function usePermLabel() {
    const { t } = useTranslation();
    return (code: string): string => t(`perm.${code}` as any, { defaultValue: code });
}

export function PermLabel({ code, showCode, className }: { code: string; showCode?: boolean; className?: string }) {
    const label = usePermLabel()(code);
    return (
        <span className={className} data-testid={`perm-label-${code}`}>
            <span>{label}</span>
            {showCode && (
                <code
                    className="ml-2 text-[10px] font-mono px-1 rounded"
                    style={{ background: "var(--input)", color: "var(--text-muted)" }}
                    title={code}
                >
                    {code}
                </code>
            )}
        </span>
    );
}

/** Read-only monospace pill for the raw code — pairs with a translated label. */
export function PermCodeHint({ code, className }: { code: string; className?: string }) {
    return (
        <code
            className={`text-[10px] font-mono px-1 rounded ${className || ""}`}
            style={{ background: "var(--input)", color: "var(--text-muted)" }}
            title={code}
            data-testid={`perm-code-${code}`}
        >
            {code}
        </code>
    );
}
